import { UseFormReturn } from 'react-hook-form';
import { BannerFormValues } from './types/banner';

import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea
} from '@@/ui/primitives';
import { LocationsField } from './LocationsField';

interface BannerConfigurationFormProps {
  form: UseFormReturn<BannerFormValues>;
}

export function BannerConfigurationForm({ form }: BannerConfigurationFormProps) {
  const { watch, setValue } = form;

  const selectedLocations = watch('selectedLocations') || [];
  const locationInput = watch('locationInput') || '';
  const bannerVisibility = !!watch('bannerVisibility');
  const hideLogin = !!watch('hideLogin');
  const severity = watch('severity') || '';
  const bannerText = watch('bannerText') || '';

  const handleLocationAdd = (location: string) => {
    if (selectedLocations.includes(location)) return;
    setValue('selectedLocations', [...selectedLocations, location], { shouldDirty: true });
    setValue('locationInput', '', { shouldDirty: false });
  };

  const handleLocationRemove = (index: number) => {
    const next = selectedLocations.filter((_, i) => i !== index);
    setValue('selectedLocations', next, { shouldDirty: true });
  };

  return (
    <div className='flex flex-col gap-5 flex-1'>
      {/* Banner Visibility */}
      <div>
        <Label className='mb-1.5'>Banner Visibility</Label>
        <div className='flex items-center gap-2'>
          <Switch
            id='property-primary'
            checked={bannerVisibility}
            onCheckedChange={(checked) => setValue('bannerVisibility', checked, { shouldDirty: true })}
            className='data-[state=checked]:bg-white data-[state=unchecked]:bg-white [&_[data-slot=switch-thumb]]:data-[state=checked]:bg-cxcentral-green'
          />
          <span className='text-[13px] font-medium'>{bannerVisibility ? 'On' : 'Off'}</span>
        </div>
      </div>

      {/* Hide Login Form */}
      <div>
        <Label className='mb-1.5'>Hide Login Form</Label>
        <div className='flex items-center gap-2'>
          <Switch
            id='property-hide-login'
            checked={hideLogin}
            onCheckedChange={(checked) => setValue('hideLogin', checked, { shouldDirty: true })}
            className='data-[state=checked]:bg-white data-[state=unchecked]:bg-white [&_[data-slot=switch-thumb]]:data-[state=checked]:bg-cxcentral-green'
          />
          <span className='text-[13px] font-medium'>{hideLogin ? 'On' : 'Off'}</span>
        </div>
      </div>

      {/* Message Severity */}
      <div>
        <Label className='mb-1.5'>Message Severity</Label>
        <Select
          value={severity}
          onValueChange={(value) => setValue('severity', value as BannerFormValues['severity'], { shouldDirty: true })}
        >
          <SelectTrigger className={`w-[200px] ${severity ? '' : 'text-[#A8ADB2]'}`} style={{ fontSize: '14px' }}>
            <SelectValue placeholder='Select Severity' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='low'>Low</SelectItem>
            <SelectItem value='moderate'>Moderate</SelectItem>
            <SelectItem value='high'>High</SelectItem>
            <SelectItem value='critical'>Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Banner Text */}
      <div className='max-w-[700px]'>
        <Label className='mb-1.5'>Banner Text</Label>
        <Textarea
          value={bannerText}
          onChange={(e) => setValue('bannerText', e.target.value, { shouldDirty: true })}
          placeholder='Write the message you want to display on the UI'
          className='min-h[100px] placeholder:text-base md:placeholder:text-sm'
        />
      </div>

      {/* Locations */}
      <div className='max-w-[700px]'>
        <Label className='mb-1.5'>Locations</Label>
        <LocationsField
          locations={selectedLocations}
          locationInput={locationInput}
          onLocationAdd={handleLocationAdd}
          onLocationRemove={handleLocationRemove}
          onLocationInputChange={(value) => setValue('locationInput', value, { shouldDirty: false })}
        />
      </div>
    </div>
  );
}
